// Money helpers — never use floats internally
export function centsToDollars(cents) {
  if (cents == null || isNaN(cents)) return "";
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(parseInt(cents, 10));
  const whole = Math.floor(abs / 100);
  const fract = abs % 100;
  return `${sign}${whole}.${String(fract).padStart(2, "0")}`;
}

export function dollarsToCents(value) {
  if (value === "" || value == null) return null;
  // Accept "25", "25.0", "25.00", "25.5"
  const s = String(value).trim();
  if (!/^\d+(\.\d{0,2})?$/.test(s)) return NaN;
  const [whole, frac = ""] = s.split(".");
  const fpadded = (frac + "00").slice(0, 2);
  return parseInt(whole, 10) * 100 + parseInt(fpadded, 10);
}

// Denver time helpers
const DENVER_TZ = "America/Denver";

const denverFmt = new Intl.DateTimeFormat("en-US", {
  timeZone: DENVER_TZ,
  year: "numeric",
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

export function utcIsoToDenverDisplay(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return denverFmt.format(d) + " MT";
  } catch {
    return iso;
  }
}

// "YYYY-MM-DDTHH:mm" (a value from <input type="datetime-local">) is naive.
// We treat that naive value as wall-clock Denver time, convert to UTC ISO.
export function denverLocalInputToUtcIso(local) {
  if (!local) return null;
  // local is "YYYY-MM-DDTHH:mm" — parse parts as Denver wall time
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(local);
  if (!m) return null;
  const [, y, mo, d, h, mi] = m.map((x, i) => (i === 0 ? x : parseInt(x, 10)));
  // Find what UTC instant has those wall-clock components in Denver.
  // Approach: start from a UTC guess assuming MST (UTC-7), then compute the
  // actual offset Denver had at that instant and correct once. (Denver is
  // never further than 7h from UTC, so a single correction suffices.)
  let guess = Date.UTC(y, mo - 1, d, h, mi);
  const offsetMin = denverOffsetMinutes(new Date(guess));
  return new Date(guess - offsetMin * 60_000).toISOString();
}

export function utcIsoToDenverLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  // Build a "YYYY-MM-DDTHH:mm" representing the Denver wall clock of d.
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: DENVER_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const get = (t) => parts.find((p) => p.type === t)?.value;
  let hh = get("hour");
  if (hh === "24") hh = "00";
  return `${get("year")}-${get("month")}-${get("day")}T${hh}:${get("minute")}`;
}

function denverOffsetMinutes(date) {
  // Returns Denver's UTC offset in minutes at this instant (negative west of UTC).
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: DENVER_TZ,
    timeZoneName: "shortOffset",
  }).formatToParts(date);
  const tz = parts.find((p) => p.type === "timeZoneName")?.value || "GMT-7";
  // e.g. "GMT-7" or "GMT-06:30"
  const m = /GMT([+-])(\d{1,2})(?::(\d{2}))?/.exec(tz);
  if (!m) return -420; // fallback MST
  const sign = m[1] === "+" ? 1 : -1;
  const h = parseInt(m[2], 10);
  const min = m[3] ? parseInt(m[3], 10) : 0;
  return sign * (h * 60 + min);
}
